import dayjs, { Dayjs } from 'dayjs';

import { TIME_FORMATS } from 'constants/timeformats';
import {
  type DateType,
  getDateTime,
  getTimestampInMS,
  timeNow,
  toUTC,
} from 'helpers/day';
import { reverse } from 'helpers/ramda';
import type { ActiveAssetTimestampsList } from 'types/optionsAnalytics';

import { PRIORITY_ORDER } from './constants';

/**
 * Sets the time of the given date to 12:00 PM.
 *
 * @param date - The date object to modify.
 * @returns A new date object with the time set to 12:00 PM.
 */
const getTwelvePM = (date: DateType): Dayjs => {
  // Ensure the date is a Dayjs instance to use the set method
  return getDateTime(date)
    .set('hour', 12)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0);
};

/**
 * Finds the exact expiry timestamp in seconds that matches the provided date.
 *
 * @param date - The date object to search for.
 * @param activeAssetExpiries - An array of expiry timestamps for the active asset.
 * @returns The matching expiry timestamp in seconds or the calculated timestamp if not found.
 */
const getMatchingExpiryTimestamp = (
  date: DateType,
  activeAssetExpiries: ActiveAssetTimestampsList
): number => {
  // Convert the updated date to a timestamp in seconds
  const dateInMS = getTimestampInMS(date);

  // Find the matching expiry timestamp in the active asset expiries array
  // If not found, return the calculated timestamp
  return activeAssetExpiries?.find(timestamp => timestamp === dateInMS) ?? dateInMS;
};

/**
 * Calculates the daily maturity expiry timestamps.
 *
 * @param baseTimestamp - The base timestamp to start calculations from.
 * @returns An object containing the daily expiry timestamps (d1, d2, d3).
 */
const getDailyMaturityExpiry = (
  baseTimestamp: Dayjs
): { d1: Dayjs; d2: Dayjs; d3: Dayjs } => {
  const currentTimeInUTC = dayjs.utc();
  const isDailyRolloverRequired = currentTimeInUTC.isAfter(baseTimestamp);

  // If the current time is past 12:00 PM today, move the timestamp to 12:00 PM tomorrow
  const d1 = isDailyRolloverRequired ? baseTimestamp.add(1, 'day') : baseTimestamp;
  const d2 = d1.add(1, 'day');
  const d3 = d1.add(2, 'days');

  return { d1, d2, d3 };
};

/**
 * Calculates the weekly maturity expiry timestamps.
 *
 * @param weekDay - The desired weekday for the expiry (0 for Sunday, 1 for Monday, ..., 6 for Saturday).
 * @param baseTimestamp - The base timestamp to start calculations from.
 * @returns An object containing the weekly expiry timestamps (w1, w2, w3).
 */
const getWeeklyMaturityExpiry = (
  weekDay: number,
  baseTimestamp: Dayjs
): { w1: Dayjs; w2: Dayjs; w3: Dayjs } => {
  // Determine if we need to roll over to the next week
  // If today is Saturday and we want Friday, we need to get next week's Friday as we can't return a past date.
  const isWeeklyRolloverRequired = baseTimestamp.day() >= weekDay;

  // Calculate the first weekly expiry timestamp (w1)
  const w1 = isWeeklyRolloverRequired
    ? getTwelvePM(baseTimestamp.endOf('week').add(1, 'day').day(weekDay))
    : getTwelvePM(baseTimestamp.day(weekDay));

  // Calculate the second weekly expiry timestamp (w2)
  const w2 = getTwelvePM(w1.endOf('week').add(1, 'day').day(weekDay));

  // Calculate the third weekly expiry timestamp (w3)
  const w3 = getTwelvePM(w2.endOf('week').add(1, 'day').day(weekDay));

  return { w1, w2, w3 };
};

/**
 * Returns a Dayjs instance set to the last weekday of the month relative to the passed Dayjs instance.
 *
 * @param dayInstance - The Dayjs instance to start calculations from.
 * @param weekDay - The desired weekday for the expiry (0 for Sunday, 1 for Monday, ..., 6 for Saturday).
 * @returns A Dayjs instance set to the last weekday of the month.
 */
const getMonthEndWeekDay = (dayInstance: Dayjs, weekDay: number): Dayjs => {
  const endOfMonthDay = dayInstance.endOf('month');
  const currentLastDay = endOfMonthDay.get('day');

  // If we want Friday and end of month is Saturday that means we can directly return that week's day.
  if (currentLastDay >= weekDay) {
    return endOfMonthDay.day(weekDay);
  }

  // If we want Friday and end of month is Tuesday then we have to go 1 week back and return weekday.
  return endOfMonthDay.subtract(1, 'week').day(weekDay);
};

/**
 * Returns Dayjs instances of monthly expiry dates.
 *
 * @param weekDay - The desired weekday for the expiry (0 for Sunday, 1 for Monday, ..., 6 for Saturday).
 * @returns An object containing the monthly expiry timestamps (m1, m2, m3).
 */
const getMonthlyMaturityExpiry = (
  weekDay: number
): { m1: Dayjs; m2: Dayjs; m3: Dayjs } => {
  const currentTimeInUTC = toUTC(timeNow());
  const thisMonthLastWeekDay = getTwelvePM(getMonthEndWeekDay(currentTimeInUTC, weekDay));

  const currentTimestampMS = currentTimeInUTC.valueOf();
  const thisMonthLastWeekdayTimestampMS = thisMonthLastWeekDay.valueOf();

  // Determine if we need to roll over to the next month
  // If the month's last weekday has already passed or if the last day is before the desired weekday.
  const isRolloverRequired = thisMonthLastWeekdayTimestampMS < currentTimestampMS;

  // Calculate the first monthly expiry timestamp (m1)
  const m1 = isRolloverRequired
    ? getTwelvePM(getMonthEndWeekDay(currentTimeInUTC.add(1, 'week'), weekDay))
    : thisMonthLastWeekDay;

  // Calculate the second monthly expiry timestamp (m2)
  const nextMonthStartDate = m1.endOf('month').add(1, 'day');
  const m2 = getTwelvePM(getMonthEndWeekDay(nextMonthStartDate, weekDay));

  // Calculate the third monthly expiry timestamp (m3)
  const m3 = getTwelvePM(getMonthEndWeekDay(m2.endOf('month').add(1, 'day'), weekDay));

  return { m1, m2, m3 };
};

/**
 * Formats expiry dates by mapping them to their corresponding labels.
 *
 * @param expiries - An object containing expiry dates.
 * @param activeAssetExpiries - An array of active asset expiry timestamps.
 * @returns An object mapping expiry timestamps to their labels.
 */
const getFormattedDates = (
  expiries: Record<string, Dayjs>,
  activeAssetExpiries: number[]
): Record<number, string> => {
  // Reverse the priority order to start with the lowest priority expiry
  const reversedPriorityOrder = reverse(PRIORITY_ORDER);

  // Reduce the reversed priority order to create a mapping of expiry timestamps to their labels
  return reversedPriorityOrder.reduce((formattedExpiries, currentLabel) => {
    const matchingTimestamp = getMatchingExpiryTimestamp(
      expiries[currentLabel],
      activeAssetExpiries
    );
    return {
      ...formattedExpiries,
      [matchingTimestamp]: currentLabel,
    };
  }, {} as Record<number, string>);
};

/**
 * Generates a formatted expiry label for display.
 *
 * @param formattedExpiry - The pre-formatted expiry string (not used in the function body)
 * @param expiryInSeconds - The expiry timestamp in seconds
 * @returns A string containing the uppercase formatted expiry and the expiry date in DD/MM/YY format
 */
const getExpiryLabel = (formattedExpiry: string, expiryInSeconds: number) => {
  return `${formattedExpiry?.toUpperCase()} - Exp: ${getDateTime(expiryInSeconds).format(
    'DD/MM/YY'
  )}`;
};

/**
 * /v2/options/taker_volume API gives UTC expiry date in DDMMYY format so we have set it to 12 PM (expiry time).
 * @param date - The expiry date in DDMMYY format
 * @returns timestamp in ms
 */
const getTakerApiDateInMs = (date: string) =>
  toUTC(date, TIME_FORMATS.DDMMYY).add(12, 'h').valueOf();

export {
  getTwelvePM,
  getMatchingExpiryTimestamp,
  getDailyMaturityExpiry,
  getWeeklyMaturityExpiry,
  getMonthlyMaturityExpiry,
  getFormattedDates,
  getExpiryLabel,
  getTakerApiDateInMs,
};
